import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  Switch,
  Alert,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from '@mui/material'
import {
  Add,
  Send,
  Campaign,
  People,
  Business,
  Security,
  SupervisorAccount,
  Email,
  PhoneAndroid,
  Notifications,
  Preview,
  PersonAdd,
  CheckBoxOutlineBlank,
  CheckBox,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const TARGET_TYPE_OPTIONS = [
  { value: 'all', label: 'All Users', icon: <People /> },
  { value: 'department', label: 'By Department', icon: <Business /> },
  { value: 'role', label: 'By Role', icon: <SupervisorAccount /> },
  { value: 'selected', label: 'Selected Users', icon: <PersonAdd /> },
  { value: 'permission', label: 'By Permission', icon: <Security /> },
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'medium', label: 'Medium', color: 'primary' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'critical', label: 'Critical', color: 'error' },
]

const columns = [
  { field: 'title', headerName: 'Title', sortable: true },
  {
    field: 'target_type',
    headerName: 'Target',
    sortable: true,
    renderCell: (value, row) => {
      const option = TARGET_TYPE_OPTIONS.find((o) => o.value === value)
      let targetDetail = ''
      if (value === 'department' && row.target_departments_names?.length) {
        targetDetail = `: ${row.target_departments_names.join(', ')}`
      } else if (value === 'role' && row.target_roles_names?.length) {
        targetDetail = `: ${row.target_roles_names.join(', ')}`
      } else if (value === 'permission' && row.target_permission) {
        targetDetail = `: ${row.target_permission}`
      } else if (value === 'selected' && row.target_users_names?.length) {
        const names = row.target_users_names.slice(0, 3).join(', ')
        const more = row.target_users_names.length > 3 ? ` +${row.target_users_names.length - 3} more` : ''
        targetDetail = `: ${names}${more}`
      }
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {option?.icon}
          <span>{option?.label || value}{targetDetail}</span>
        </Box>
      )
    },
  },
  {
    field: 'priority',
    headerName: 'Priority',
    sortable: true,
    width: 100,
    renderCell: (value) => {
      const option = PRIORITY_OPTIONS.find((o) => o.value === value)
      return (
        <Chip
          label={option?.label || value}
          color={option?.color || 'default'}
          size="small"
        />
      )
    },
  },
  {
    field: 'is_sent',
    headerName: 'Status',
    sortable: true,
    width: 100,
    renderCell: (value, row) => (
      <Chip
        label={value ? 'Sent' : 'Draft'}
        color={value ? 'success' : 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'recipient_count',
    headerName: 'Recipients',
    sortable: true,
    width: 100,
    renderCell: (value) => value || 0,
  },
  { field: 'created_by_name', headerName: 'Created By', sortable: true },
  {
    field: 'created_at',
    headerName: 'Created',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  {
    field: 'sent_at',
    headerName: 'Sent At',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleString() : '-',
  },
]

const initialFormData = {
  title: '',
  message: '',
  priority: 'medium',
  target_type: 'all',
  target_departments: [],
  target_roles: [],
  target_user_ids: [],
  target_permission: '',
  send_email: true,
  send_push: true,
  send_in_app: true,
  link: '',
}

const BroadcastNotifications = () => {
  const { hasPermission } = usePermissions()
  const [broadcasts, setBroadcasts] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [selectedBroadcast, setSelectedBroadcast] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [previewRecipients, setPreviewRecipients] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // Options for dropdowns
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [users, setUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')

  const fetchBroadcasts = async () => {
    try {
      setLoading(true)
      const response = await api.get('/notifications/broadcasts/')
      setBroadcasts(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching broadcasts:', error)
      toast.error('Failed to load broadcasts')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [deptsRes, rolesRes, usersRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/common/roles/'),
        api.get('/common/profiles/', { params: { page_size: 100 } }),
      ])
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
      setUsers(usersRes.data.results || usersRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  const searchUsers = async (query) => {
    if (!query || query.length < 2) return
    setUserSearchLoading(true)
    try {
      const response = await api.get('/common/profiles/', { params: { search: query } })
      setUsers(response.data.results || response.data)
    } catch (error) {
      console.error('Error searching users:', error)
    } finally {
      setUserSearchLoading(false)
    }
  }

  useEffect(() => {
    fetchBroadcasts()
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!formData.title || !formData.message) {
      setErrors({
        title: !formData.title ? 'Title is required' : '',
        message: !formData.message ? 'Message is required' : '',
      })
      return
    }

    setFormLoading(true)
    try {
      const payload = { ...formData }
      // Clean up based on target type
      if (formData.target_type !== 'department') {
        payload.target_departments = []
      }
      if (formData.target_type !== 'role') {
        payload.target_roles = []
      }
      if (formData.target_type !== 'permission') {
        payload.target_permission = ''
      }
      if (formData.target_type !== 'selected') {
        payload.target_user_ids = []
      } else {
        // Make sure we're sending user IDs
        payload.target_user_ids = selectedUsers.map(u => u.user_id || u.id)
      }

      await api.post('/notifications/broadcasts/', payload)
      toast.success('Broadcast created successfully')
      fetchBroadcasts()
      setFormOpen(false)
      setFormData(initialFormData)
      setSelectedUsers([])
    } catch (error) {
      console.error('Error creating broadcast:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
      toast.error('Failed to create broadcast')
    } finally {
      setFormLoading(false)
    }
  }

  const handleSend = async (broadcastId) => {
    setSendLoading(true)
    try {
      const response = await api.post(`/notifications/broadcasts/${broadcastId}/send/`)
      toast.success(`Broadcast sent to ${response.data.total_recipients} recipients`)
      fetchBroadcasts()
      setDetailOpen(false)
    } catch (error) {
      console.error('Error sending broadcast:', error)
      toast.error(error.response?.data?.error || 'Failed to send broadcast')
    } finally {
      setSendLoading(false)
    }
  }

  const handlePreviewRecipients = async (broadcastId) => {
    setPreviewLoading(true)
    setPreviewOpen(true)
    try {
      const response = await api.get(`/notifications/broadcasts/${broadcastId}/preview_recipients/`)
      setPreviewRecipients(response.data)
    } catch (error) {
      console.error('Error fetching recipients:', error)
      toast.error('Failed to load recipients preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleRowClick = (row) => {
    setSelectedBroadcast(row)
    setDetailOpen(true)
  }

  const handleDelete = async (broadcastId) => {
    if (!window.confirm('Are you sure you want to delete this broadcast?')) {
      return
    }
    try {
      await api.delete(`/notifications/broadcasts/${broadcastId}/`)
      toast.success('Broadcast deleted')
      fetchBroadcasts()
      setDetailOpen(false)
    } catch (error) {
      console.error('Error deleting broadcast:', error)
      toast.error('Failed to delete broadcast')
    }
  }

  return (
    <Box>
      <PageHeader
        title="Broadcast Notifications"
        subtitle="Send notifications to departments, roles, or all users"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Notifications', path: '/notifications' },
          { label: 'Broadcasts' },
        ]}
        actions={
          <PermissionGate permission="notifications.manage">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              New Broadcast
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={broadcasts}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search broadcasts..."
          emptyMessage="No broadcasts found"
        />
      </Card>

      {/* Create Broadcast Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Campaign color="primary" />
            Create Broadcast Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={Boolean(errors.title)}
                helperText={errors.title}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={4}
                error={Boolean(errors.message)}
                helperText={errors.message}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priority"
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip
                        label={option.label}
                        color={option.color}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  name="target_type"
                  value={formData.target_type}
                  onChange={handleChange}
                  label="Target Audience"
                >
                  {TARGET_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {formData.target_type === 'department' && (
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(errors.target_departments)}>
                  <InputLabel>Target Departments</InputLabel>
                  <Select
                    multiple
                    name="target_departments"
                    value={formData.target_departments}
                    onChange={handleChange}
                    label="Target Departments"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const dept = departments.find((d) => d.id === id)
                          return <Chip key={id} label={dept?.name || id} size="small" />
                        })}
                      </Box>
                    )}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formData.target_type === 'role' && (
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(errors.target_roles)}>
                  <InputLabel>Target Roles</InputLabel>
                  <Select
                    multiple
                    name="target_roles"
                    value={formData.target_roles}
                    onChange={handleChange}
                    label="Target Roles"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const role = roles.find((r) => r.id === id)
                          return <Chip key={id} label={role?.name || id} size="small" />
                        })}
                      </Box>
                    )}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formData.target_type === 'selected' && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={users}
                  value={selectedUsers}
                  onChange={(event, newValue) => {
                    setSelectedUsers(newValue)
                  }}
                  onInputChange={(event, newInputValue) => {
                    setUserSearchQuery(newInputValue)
                    if (newInputValue.length >= 2) {
                      searchUsers(newInputValue)
                    }
                  }}
                  getOptionLabel={(option) => {
                    const name = option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username
                    return `${name} (${option.email || option.username})`
                  }}
                  isOptionEqualToValue={(option, value) => (option.user_id || option.id) === (value.user_id || value.id)}
                  loading={userSearchLoading}
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                        checkedIcon={<CheckBox fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      <Box>
                        <Typography variant="body2">
                          {option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} {option.department_name && `• ${option.department_name}`}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.user_id || option.id}
                        label={option.full_name || option.username}
                        size="small"
                        avatar={<Avatar sx={{ width: 24, height: 24 }}>{(option.full_name || option.username)?.charAt(0)}</Avatar>}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Users"
                      placeholder="Search users by name or email..."
                      error={Boolean(errors.target_user_ids)}
                      helperText={errors.target_user_ids || `${selectedUsers.length} user(s) selected`}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {formData.target_type === 'permission' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Permission Code"
                  name="target_permission"
                  value={formData.target_permission}
                  onChange={handleChange}
                  error={Boolean(errors.target_permission)}
                  helperText={errors.target_permission || 'e.g., work_orders.manage, assets.edit'}
                  placeholder="e.g., work_orders.manage"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link (optional)"
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="/work-orders/123"
                helperText="Optional link to include in notification"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Delivery Channels
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                FCM-first: Users with mobile app will receive push notifications. Others will receive email.
              </Alert>
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.send_push}
                      onChange={handleSwitchChange}
                      name="send_push"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <PhoneAndroid fontSize="small" /> Push Notification
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.send_email}
                      onChange={handleSwitchChange}
                      name="send_email"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Email fontSize="small" /> Email
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.send_in_app}
                      onChange={handleSwitchChange}
                      name="send_in_app"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Notifications fontSize="small" /> In-App
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Create Broadcast
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Campaign color="primary" />
              Broadcast Details
            </Box>
            <Chip
              label={selectedBroadcast?.is_sent ? 'Sent' : 'Draft'}
              color={selectedBroadcast?.is_sent ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBroadcast && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedBroadcast.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {selectedBroadcast.message}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Box>
                  <Chip
                    label={selectedBroadcast.priority}
                    color={PRIORITY_OPTIONS.find((o) => o.value === selectedBroadcast.priority)?.color || 'default'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Target</Typography>
                <Typography variant="body2">
                  {TARGET_TYPE_OPTIONS.find((o) => o.value === selectedBroadcast.target_type)?.label}
                </Typography>
              </Grid>
              {selectedBroadcast.target_departments_names?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Departments</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {selectedBroadcast.target_departments_names.map((name, i) => (
                      <Chip key={i} label={name} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {selectedBroadcast.target_roles_names?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Roles</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {selectedBroadcast.target_roles_names.map((name, i) => (
                      <Chip key={i} label={name} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {selectedBroadcast.target_users_names?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Selected Users</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {selectedBroadcast.target_users_names.map((name, i) => (
                      <Chip key={i} label={name} size="small" variant="outlined" icon={<PersonAdd />} />
                    ))}
                  </Box>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Recipients</Typography>
                <Typography variant="body2">{selectedBroadcast.recipient_count || 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Created By</Typography>
                <Typography variant="body2">{selectedBroadcast.created_by_name}</Typography>
              </Grid>
              {selectedBroadcast.is_sent && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Emails Sent</Typography>
                    <Typography variant="body2">{selectedBroadcast.emails_sent}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Push Sent</Typography>
                    <Typography variant="body2">{selectedBroadcast.push_sent}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Sent At</Typography>
                    <Typography variant="body2">
                      {selectedBroadcast.sent_at
                        ? new Date(selectedBroadcast.sent_at).toLocaleString()
                        : '-'}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <Chip
                    icon={<PhoneAndroid />}
                    label="Push"
                    size="small"
                    color={selectedBroadcast.send_push ? 'success' : 'default'}
                    variant={selectedBroadcast.send_push ? 'filled' : 'outlined'}
                  />
                  <Chip
                    icon={<Email />}
                    label="Email"
                    size="small"
                    color={selectedBroadcast.send_email ? 'success' : 'default'}
                    variant={selectedBroadcast.send_email ? 'filled' : 'outlined'}
                  />
                  <Chip
                    icon={<Notifications />}
                    label="In-App"
                    size="small"
                    color={selectedBroadcast.send_in_app ? 'success' : 'default'}
                    variant={selectedBroadcast.send_in_app ? 'filled' : 'outlined'}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {!selectedBroadcast?.is_sent && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDelete(selectedBroadcast?.id)}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Preview />}
            onClick={() => handlePreviewRecipients(selectedBroadcast?.id)}
          >
            Preview Recipients
          </Button>
          <Box flex={1} />
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {!selectedBroadcast?.is_sent && (
            <PermissionGate permission="notifications.manage">
              <Button
                startIcon={<Send />}
                color="success"
                onClick={() => handleSend(selectedBroadcast?.id)}
                loading={sendLoading}
              >
                Send Now
              </Button>
            </PermissionGate>
          )}
        </DialogActions>
      </Dialog>

      {/* Recipients Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <People color="primary" />
            Recipients Preview
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">Loading recipients...</Typography>
            </Box>
          ) : previewRecipients ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Showing {previewRecipients.showing} of {previewRecipients.total_recipients} recipients
              </Alert>
              <List dense>
                {previewRecipients.sample_recipients?.map((recipient) => (
                  <ListItem key={recipient.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#1B5E7A', width: 32, height: 32 }}>
                        {recipient.name?.charAt(0) || '?'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={recipient.name}
                      secondary={
                        <>
                          {recipient.email}
                          {recipient.department && ` - ${recipient.department}`}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No recipients found</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BroadcastNotifications
